import axios from 'axios';
import PropTypes from 'prop-types';
import React from 'react';
import ReactDOM from 'react-dom';

import { ArticleState, LeadItNews } from './LeadItNews';
import oewaMapping from './oewaMapping.json';

const FORMAT_FIX_IDS = ['22373', '23175', '23176', '48345', '48346', '23367'];

const FORMAT_RESPONSIVE_IDS = ['23362', '23366', '23167', '48368', '48369'];

const FORMAT_NAMES = {};

// We could use two `Set`s instead, but they are not fully supported in IE.
for (const id of FORMAT_FIX_IDS) {
  FORMAT_NAMES[id] = 'fix';
}

for (const id of FORMAT_RESPONSIVE_IDS) {
  FORMAT_NAMES[id] = 'responsive';
}

const gaName = 'bruceleads';

const gaEventQueue = [];

let isGaInitialized = false;
let previousTrackingId = null;

function initializeGa(articleData) {
  const trackingId = articleData.tracking_id;

  if (previousTrackingId && previousTrackingId !== trackingId) {
    // TODO: should we support this case and create a separate tracker?
    throw new Error('GA tracking id changed');
  }

  previousTrackingId = trackingId;

  window.ga('create', {
    trackingId,
    cookieDomain: 'auto',
    name: gaName,
  });

  // if (window.bruceLeadsGaDebug === true) {
  //   window.ga_debug = { trace: true };
  // }

  window.ga(`${gaName}.send`, 'pageview', {
    dimension1: articleData.cid,
  });

  gaEventQueue.forEach((event) => {
    window.ga(`${gaName}.send`, 'event', event);
  });

  gaEventQueue.length = 0;

  isGaInitialized = true;
}

// Load google analytics
if (window.ga == null) {
  // Based on the GA snippet:
  // https://developers.google.com/analytics/devguides/collection/analyticsjs#alternative_async_tag
  const ga = function ga() {
    (ga.q = ga.q || []).push(arguments);
  };
  ga.l = +new Date();
  window.ga = ga;

  // const gaUrl = window.bruceLeadsGaDebug;
  // ? 'https://www.google-analytics.com/analytics_debug.js'
  // : 'https://www.google-analytics.com/analytics.js';
  // const gaLoader = document.createElement('script');
  // gaLoader.src = gaUrl;
  // gaLoader.async = true;
  // document.head.appendChild(gaLoader);
}

function sendGaEvent(event) {
  if (isGaInitialized) {
    window.ga(`${gaName}.send`, 'event', event);
  } else {
    // It shouldn't happen. Maybe we should throw an error instead?
    gaEventQueue.push(event);
  }
}

function createFetcherCallbacks({ internalEventsHost, useSmartAnalytics }) {
  function sendSmartEvent(type) {
    if (!useSmartAnalytics) {
      return;
    }

    const iframeParams = new URLSearchParams(window.location.search);
    const pageId = iframeParams.get('pid');
    const insertionId = iframeParams.get('iid');
    const creativeId = iframeParams.get('cid');

    if (!(pageId && insertionId && creativeId)) {
      return;
    }

    const pixelParams = new URLSearchParams({
      // Any numeric value for the `sid` field is expected by the platform. Provide a random one
      sid: 14284234,
      pid: pageId,
      iid: insertionId,
      cid: creativeId,
      ts: Date.now(),
      key: type,
    });
    const pixelUrl = `https://styria.smartadserver.com/track/action?${pixelParams.toString()}`;
    const pixel = new Image();
    pixel.src = pixelUrl;
  }

  function sendInternalEvent(articleId, type) {
    const url = `${internalEventsHost}/articles/${articleId}/report-${type}`;
    // Internal event tracking. We use `navigator.sendBeacon` here, which is
    // a recommended solution for analytics requests.
    if (typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(url);
    } else {
      // TODO: remove after we stopped supporting IE
      axios.post(url);
    }
  }

  // Send ad capping request to set up a 3rd party cookie
  function sendAdCappingRequest(adId) {
    if (!useSmartAnalytics) {
      return;
    }

    axios.get('https://styria.smartadserver.com/t/partner', {
      params: {
        partnername: adId,
        value: `${adId}=submitted`,
        expdate: 30,
      },
    });
  }

  return {
    onClickOnce: (campaignId, articleId) => {
      sendInternalEvent(articleId, 'click');
      sendSmartEvent('bl_click');
    },

    onFormChangeOnce: (campaignId, articleId) => {
      sendInternalEvent(articleId, 'form-change');
      sendSmartEvent('bl_form_change');
    },

    onInteraction: (campaignId, articleId) => {
      sendGaEvent({
        eventCategory: 'clicks',
        eventAction: campaignId,
        eventLabel: articleId,
        nonInteraction: true,
      });

      sendInternalEvent(articleId, 'form-show');
      sendSmartEvent('bl_form_show');
    },

    onImpression: (campaignId, articleId) => {
      sendGaEvent({
        eventCategory: 'impressions',
        eventAction: campaignId,
        eventLabel: articleId,
        nonInteraction: true,
      });

      sendSmartEvent('bl_impression');
    },

    onSubmit: (submitUrl, formData, articleData, done) => {
      axios
        .post(
          submitUrl,
          {
            data: formData,
            token: articleData.submit_token,
          },
          {
            withCredentials: true, // this is needed so we store the cookies
          }
        )
        .then(() => {
          done();
          sendGaEvent({
            eventCategory: 'submissions',
            eventAction: articleData.cid,
            eventLabel: articleData.aid,
            nonInteraction: true,
          });
          sendAdCappingRequest(articleData.aid);
        });

      sendSmartEvent('bl_submission');
    },
  };
}

/**
 * Translates the oewa category into an interest_id (IAB) for kleinezeitung.at
 */
const getIABIdFromKlz = () => {
  if (window.dataLayer && window.dataLayer[0]) {
    const oewaPathLower = window.dataLayer[0].oewaPath.toLowerCase();
    const iabId = oewaMapping[oewaPathLower];
    if (iabId === undefined) {
      return null;
    }
    return iabId;
  }
  return null;
};

/**
 * gets the region for kleinzeitung.at
 */
const getRegionsFromKlz = () => {
  if (window.KLZ && window.KLZ.settings.region.id !== '') {
    return window.KLZ.settings.region.id;
  }
  return null;
};

// A simplified version of `once` from lodash - it ensures that the function (given
// as the `callback` argument) will be called only once.
function once(callback) {
  let called = false;

  return () => {
    if (called) {
      return;
    }

    callback();
    called = true;
  };
}

class ArticleFetcher extends React.Component {
  constructor() {
    super();
    this.state = {
      articleData: null,
      articleState: ArticleState.BANNER,
      quizAnswer: undefined, // assigned if relevant
    };

    this.handleClickOnce = once(() => {
      const { cid, aid } = this.state.articleData;
      this.props.onClickOnce(cid, aid);
    });

    this.handleFormChangeOnce = once(() => {
      const { cid, aid } = this.state.articleData;
      this.props.onFormChangeOnce(cid, aid);
    });
  }

  setArticleData(articleData) {
    this.setState({ articleData });

    if (articleData.teaser_display_mode === 'NONE') {
      this.setState({ articleState: ArticleState.FORM });
    }
  }

  componentDidMount() {
    const { baseUrl, articleId, urlParameters, fetchUrl } = this.props;
    let { articleData } = this.props;
    if (articleData != null) {
      this.setArticleData(articleData);
      this.startGa(articleData);
      return;
    }

    let url = fetchUrl || `${baseUrl}/get`;
    if (articleId) {
      url += `/${articleId}`;
    }

    axios
      .get(url, {
        // this is needed so we store the cookies
        withCredentials: true,
        params: {
          // TODO: no longer supported
          interest_id: getIABIdFromKlz(),
          region_id: getRegionsFromKlz(),
          ...urlParameters,
        },
      })
      .then((response) => {
        articleData = response.data;

        this.setArticleData(articleData);
        this.startGa(articleData);
      });
  }

  startGa(articleData) {
    if (articleData.tracking_id) {
      initializeGa(articleData);
    }

    const { cid, aid } = articleData;
    this.props.onImpression(cid, aid);
  }

  handleSubmitForm = (event) => {
    event.preventDefault();

    const { articleData } = this.state;
    const { resultUrl, baseUrl } = this.props;

    // convert form to object
    const formData = new FormData(event.target);
    const data = {};

    const promises = [];

    formData.forEach((value, key) => {
      // if it is a boolean value convert checkbox val into boolean
      if (articleData.form_schema.properties[key].type === 'boolean') {
        data[key] = value === 'on';
      } else if (
        // Convert numeric values to actual numbers
        articleData.form_schema.properties[key].type === 'number'
      ) {
        if (value) {
          const parsedNumber = Number(value);
          if (!Number.isNaN(parsedNumber)) {
            data[key] = parsedNumber;
          }
        }
      } else if (
        articleData.form_schema.properties[key].type === 'string' &&
        articleData.form_schema.properties[key].format === 'binary'
      ) {
        promises.push(
          // Inspired by https://stackoverflow.com/a/57272491
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(value);
            reader.onload = () => {
              // const [, encoded] = reader.result.split(';base64,');
              data[key] = reader.result;
              resolve();
            };
            reader.onerror = (error) => {
              reject(error);
            };
          })
        );
      } else {
        data[key] = value;
      }
    });

    if (articleData.type === 'QUIZ') {
      // also submit the quiz answer
      data.quizAnswer = this.state.quizAnswer;
    }

    const done = () => {
      this.setState({ articleState: ArticleState.SUBMIT_SCREEN });
    };
    const postUrl = resultUrl || `${baseUrl}/result`;
    Promise.all(promises).then(() => {
      this.props.onSubmit(postUrl, data, articleData, done);
    });
  };

  handleClose = () => {
    this.setState({ articleState: ArticleState.BANNER });

    this.props.onClose?.();
  };

  handleShowForm = (origQuizAnswer) => {
    const { cid, aid, type } = this.state.articleData;
    const quizAnswer = type === 'QUIZ' ? origQuizAnswer : null;

    this.props.onInteraction(cid, aid);

    this.setState({ articleState: ArticleState.FORM, quizAnswer });
  };

  handleInputFocus = () => {
    // this is only for single field inputs,
    // we count a input focus as an interaction
    const { cid, aid } = this.state.articleData;
    this.props.onInteraction(cid, aid);
  };

  render() {
    const { articleData, articleState } = this.state;
    const { tosText } = this.props;
    if (articleData) {
      return (
        <form
          onChange={this.handleFormChangeOnce}
          onClick={this.handleClickOnce}
          onSubmit={this.handleSubmitForm}
        >
          <LeadItNews
            articleData={articleData}
            articleState={articleState}
            onClose={this.handleClose}
            onShowForm={this.handleShowForm}
            onInputFocus={this.handleInputFocus}
            styleName={this.props.styleName}
            tosText={tosText}
          />
        </form>
      );
    }
    return null;
  }
}

ArticleFetcher.propTypes = {
  tosText: PropTypes.string,
  onClickOnce: PropTypes.func,
  onFormChangeOnce: PropTypes.func,
  onSubmit: PropTypes.func,
  onInteraction: PropTypes.func,
  onImpression: PropTypes.func,
  styleName: PropTypes.string,
  // if set it is used to fetch the article, instead of adding `/get` to the base url
  fetchUrl: PropTypes.string,
  resultUrl: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  articleData: PropTypes.object,
};

ArticleFetcher.defaultProps = {
  tosText: 'Mit der Anmeldung stimme ich den {link} zu.',
  styleName: 'base',
  articleData: null,
  fetchUrl: null,
};

const renderArticle = (element, options) => {
  if (!window.requestAnimationFrame) {
    // IE < 10 or other very old browsers
    return;
  }

  if (!window.URL) {
    // IE 11 or other older browsers
    return;
  }

  if (!options.baseUrl) {
    // eslint-disable-next-line no-console
    console.debug('baseUrl is not defined');
    return;
  }

  const baseUrl = new URL(options.baseUrl);

  ReactDOM.render(
    <ArticleFetcher
      {...options}
      {...createFetcherCallbacks({
        internalEventsHost: baseUrl.origin,
      })}
    />,
    element
  );
};

function renderArticleInsideFrame(element, options) {
  if (!window.requestAnimationFrame) {
    // IE < 10 or other very old browsers
    return;
  }

  const startRendering = () => {
    const searchParams = new URLSearchParams(window.location.search);

    const siteId = searchParams.get('site_id');
    const formatId = searchParams.get('format_id');

    let styleName = '_fallback';

    if (siteId && formatId) {
      const formatName = FORMAT_NAMES[formatId];
      if (formatName) {
        styleName = `${siteId}_${formatName}`;
      }
    }

    ReactDOM.render(
      <ArticleFetcher
        {...options}
        {...createFetcherCallbacks({
          internalEventsHost: '',
          useSmartAnalytics: true,
        })}
        onClose={() => sendCollapse(formatId)}
        styleName={styleName}
      />,
      element
    );

    sendExpandOnClick(formatId);
  };

  if ('URLSearchParams' in window) {
    // Modern browsers
    startRendering();
  } else {
    // Older browsers requiring polyfills
    import('core-js/stable/url-search-params').then(() => {
      startRendering();
    });
  }
}

function sendExpandOnClick(formatId) {
  if (!formatId) {
    return;
  }

  if (window.parent === window) {
    return;
  }

  const callback = () => {
    window.parent.postMessage(`pub_ist_expand_${formatId}`, '*');

    // Send the message only once
    document.body.removeEventListener('click', callback, false);
  };

  document.body.addEventListener('click', callback, false);
}

function sendCollapse(formatId) {
  if (!formatId) {
    return;
  }

  if (window.parent === window) {
    return;
  }

  window.parent.postMessage(`pub_ist_collapse_${formatId}`, '*');

  // Make expanding work again
  sendExpandOnClick(formatId);
}

window.LeadIt = { renderArticle, renderArticleInsideFrame };

export { LeadItNews, renderArticle };
